import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { BadgeRenderer } from './BadgeRenderer';
import { PageLoader } from '../ui/PageLoader';
import { PageHeader } from '../ui/PageHeader';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import PageBackLink from '../ui/PageBackLink';
import { vehicleProfilePath } from '../../utils/vehicleUrls';

const BadgeDetailPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!publicId) return;
    setLoading(true);
    try {
      setData(await apiService.getBadgeDetail(publicId));
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Badge not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <PageLoader message="Loading badge" />;

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
        <div className="max-w-lg mx-auto px-4 py-16">
          <PageBackLink label="Back to Bilt" to="/" className="mb-6" />
          <EmptyState
            title="Badge not found"
            description={error || 'This badge may have been revoked or removed.'}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => void load()}>
                Try again
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (data.type === 'definition') {
    const earners = data.earners || [];
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PageBackLink label="Back to Discover" to="/discover" className="mb-4" />
          <PageHeader eyebrow="Badge" title={data.definition.name} description={data.definition.description || 'Public badge definition'} />
          <BadgeRenderer design={data.definition} size="lg" />
          <h2 className="heading-section text-lg sm:text-xl mt-8 mb-3">Vehicles that earned this</h2>
          {earners.length === 0 ? (
            <p className="text-sm text-gray-500">No public builds have earned this badge yet.</p>
          ) : (
            <ul className="space-y-2">
              {earners.map((e: any) => (
                <li key={e.id}>
                  <Link
                    className="text-orange-400 hover:text-orange-300 min-h-[44px] inline-flex items-center"
                    to={vehicleProfilePath(e.owner_username, e.public_slug)}
                  >
                    {e.year} {e.make} {e.model}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  const badge = data.badge;
  const issuerName =
    badge.issuer?.event?.title || badge.issuer?.club?.name || 'Bilt';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageBackLink label="Back to Bilt" to="/" className="mb-6" />
        <div className="flex flex-col items-center text-center">
          <BadgeRenderer design={badge.design} size="lg" />
          <h1 className="heading-display text-2xl sm:text-3xl mt-4">{badge.design?.name}</h1>
          <p className="text-muted text-sm mt-2">{badge.description}</p>
        </div>
        <dl className="mt-8 space-y-3 text-sm">
          <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
            <dt className="text-gray-500">Issuer</dt>
            <dd className="text-gray-200">{issuerName}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
            <dt className="text-gray-500">Verification</dt>
            <dd className="text-gray-200 capitalize">{String(badge.verification_type || '').replace(/_/g, ' ')}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
            <dt className="text-gray-500">Earned</dt>
            <dd className="text-gray-200">{badge.issued_at ? new Date(badge.issued_at).toLocaleDateString() : '-'}</dd>
          </div>
          <div className="flex justify-between gap-4 border-b border-white/[0.06] py-2">
            <dt className="text-gray-500">Status</dt>
            <dd className="text-gray-200">{badge.is_active ? 'Active' : 'Revoked'}</dd>
          </div>
        </dl>
        {badge.vehicle && badge.vehicle.public_slug && (badge.vehicle as any).owner_username ? (
          <Link
            className="mt-6 block w-full text-center btn-secondary min-h-[44px] leading-[44px]"
            to={vehicleProfilePath((badge.vehicle as any).owner_username, badge.vehicle.public_slug)}
          >
            View vehicle
          </Link>
        ) : null}
        {badge.issuer?.event?.slug ? (
          <Link className="block text-center mt-3 text-orange-400 min-h-[44px]" to={`/events/${badge.issuer.event.slug}/archive`}>
            View event archive
          </Link>
        ) : null}
        {badge.issuer?.club?.slug ? (
          <Link className="block text-center mt-3 text-orange-400 min-h-[44px]" to={`/clubs/${badge.issuer.club.slug}`}>
            View club
          </Link>
        ) : null}
        <Button
          className="mt-4 w-full min-h-[44px]"
          variant="ghost"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
            } catch {
              /* ignore */
            }
          }}
        >
          Copy badge link
        </Button>
      </div>
    </div>
  );
};

export default BadgeDetailPage;
